/**
 * Job polling.
 *
 * `poll_after_ms` is the SERVER'S instruction and this client obeys it. That is
 * how the poll rate is turned down under load without shipping frontend code,
 * and it is null exactly when the job is terminal — which is also the signal to
 * stop. There is no client-side interval constant to disagree with it.
 *
 * Three things the contract does not specify and this module decides, because
 * they are client behaviour rather than protocol:
 *
 *   1. **Hidden tabs stop polling.** Background timers are throttled to about
 *      one tick a minute anyway, so a "polite" interval in a hidden tab is
 *      neither polite nor an interval — it is an unpredictable one. Stopping
 *      and doing one immediate poll on becoming visible is both cheaper for
 *      the server and fresher for the user, who sees current state the moment
 *      they look at it.
 *   2. **Network failures back off, they do not stop.** A dropped connection
 *      is not a terminal job. The delay doubles from the last server-directed
 *      interval up to a ceiling, with jitter, so a server coming back from an
 *      outage is not hit by every waiting tab in the same 50 ms.
 *   3. **`pagehide` aborts.** An in-flight fetch can keep a page out of the
 *      back/forward cache; aborting on pagehide and restarting on pageshow
 *      keeps the queue page bfcache-eligible, which matters because "come back
 *      to this link later" is copy we shipped.
 */

import { ApiError, getJob } from './api.ts';
import type { JobResponse } from './api-types.ts';

const MAX_BACKOFF_MS = 30_000;
const FALLBACK_INTERVAL_MS = 3_000;

export interface PollHandle {
  stop: () => void;
}

export interface PollCallbacks {
  onUpdate: (job: JobResponse) => void;
  /** Terminal: done | failed | dead. Polling has already stopped. */
  onTerminal: (job: JobResponse) => void;
  /** A poll failed. Polling continues with backoff; return nothing. */
  onError?: (err: ApiError) => void;
}

function jitter(ms: number): number {
  // Full jitter: uniform in [ms/2, ms]. Enough to decorrelate clients without
  // making the visible countdown lurch.
  return Math.round(ms / 2 + Math.random() * (ms / 2));
}

export function pollJob(jobId: string, callbacks: PollCallbacks): PollHandle {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let controller: AbortController | null = null;
  let failures = 0;
  let lastInterval = FALLBACK_INTERVAL_MS;

  const clearTimer = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const schedule = (ms: number) => {
    clearTimer();
    if (stopped || document.visibilityState === 'hidden') return;
    timer = setTimeout(() => void tick(), ms);
  };

  const tick = async (): Promise<void> => {
    if (stopped) return;
    controller = new AbortController();
    try {
      const job = await getJob(jobId, controller.signal);
      failures = 0;
      callbacks.onUpdate(job);
      if (job.poll_after_ms === null) {
        stopped = true;
        clearTimer();
        callbacks.onTerminal(job);
        return;
      }
      lastInterval = Math.max(500, job.poll_after_ms);
      schedule(lastInterval);
    } catch (err) {
      if (stopped) return;
      if (err instanceof ApiError) {
        // A 404 is terminal in the only way that matters: there is nothing to
        // poll. Anything else is worth retrying.
        if (err.problem.status === 404) {
          stopped = true;
          clearTimer();
          callbacks.onError?.(err);
          return;
        }
        callbacks.onError?.(err);
      }
      failures += 1;
      const backoff = Math.min(MAX_BACKOFF_MS, lastInterval * 2 ** failures);
      schedule(jitter(backoff));
    } finally {
      controller = null;
    }
  };

  const onVisibility = () => {
    if (stopped) return;
    if (document.visibilityState === 'visible') {
      // Immediate, not scheduled: the user is looking at it right now.
      void tick();
    } else {
      clearTimer();
      controller?.abort();
    }
  };

  const onPageHide = () => {
    clearTimer();
    controller?.abort();
  };

  const onPageShow = () => {
    if (!stopped && document.visibilityState === 'visible') void tick();
  };

  document.addEventListener('visibilitychange', onVisibility);
  addEventListener('pagehide', onPageHide);
  addEventListener('pageshow', onPageShow);

  void tick();

  return {
    stop() {
      stopped = true;
      clearTimer();
      controller?.abort();
      document.removeEventListener('visibilitychange', onVisibility);
      removeEventListener('pagehide', onPageHide);
      removeEventListener('pageshow', onPageShow);
    },
  };
}
